// Central SEO / AEO configuration: page titles, descriptions and structured data (JSON-LD).
// Applied at runtime by <SeoManager /> and baked into static HTML by scripts/prerender.js.
import { PRODUCTS, CATALYST_FAQ, SUITE } from './Data/catalyst';
import { SERVICES } from './Data/services';
import { HOME_FAQ } from './Data/faqs';

export const SITE_URL = 'https://www.zemanticsllc.com';
export const SITE_NAME = 'Zemantics';
export const OG_IMAGE = `${SITE_URL}/og-image.png`;

export interface PageSeo {
  title: string;
  description: string;
  name: string;               // breadcrumb / page name
  noindex?: boolean;
  parent?: { name: string; path: string };
  service?: { name: string; type: string };
  products?: boolean;
  pageType?: 'ContactPage' | 'AboutPage';
  faq?: { q: string; a: string }[];
}

export const SEO: Record<string, PageSeo> = {
  '/': {
    title: 'Zemantics | Agentic AI & Enterprise AI Solutions',
    description:
      'Zemantics builds AI agents, agentic orchestrators and data decision pipelines that run enterprise operations, plus AI and software engineering across industries.',
    name: 'Home',
    faq: HOME_FAQ,
  },
  '/products': {
    title: 'Zemantics Catalyst Suite | AI Decision Support Products',
    description:
      'Explore Catalyst Research, Curriculum and Workforce: AI-powered, evidence-linked decision support with human review for research, academia and workforce.',
    name: 'Products',
    products: true,
    faq: CATALYST_FAQ,
  },
  '/company': {
    title: 'About Zemantics | AI & Software Engineering Company',
    description:
      'Zemantics LLC is an AI and software engineering company building AI agents, orchestrators and software across industries, with 175+ products built over 15+ years.',
    name: 'Company',
    pageType: 'AboutPage',
  },
  '/contact': {
    title: 'Contact Zemantics | Start Your AI Project',
    description:
      'Contact Zemantics to discuss AI agents, agentic orchestration, data engineering, generative AI, software engineering for AI products or the Catalyst Suite.',
    name: 'Contact Us',
    pageType: 'ContactPage',
  },
  '/aiml': {
    title: 'AI & Machine Learning Development | Zemantics',
    description:
      'End-to-end AI and ML engineering: strategy, custom models, agentic AI, API-first integration and production MLOps, built cloud-native and secure by design.',
    name: 'Artificial Intelligence And ML',
    parent: { name: 'Our Services', path: '/' },
    service: { name: 'Artificial Intelligence and Machine Learning Development', type: 'AI and machine learning development' },
  },
  '/dataengineering': {
    title: 'Data Engineering & Data Science Services | Zemantics',
    description:
      'Data engineering and data science services: data gathering and preprocessing, annotation, model training and validation, evaluation and outcome assessment.',
    name: 'Data Engineering',
    parent: { name: 'Our Services', path: '/' },
    service: { name: 'Data Engineering', type: 'Data engineering and data science' },
  },
  '/generativeai': {
    title: 'Generative AI Development Services | Zemantics',
    description:
      'Generative and agentic AI development: RAG, domain-specific models and guardrails, evaluated for quality and safety, deployed via CI/CD and monitored.',
    name: 'Generative AI',
    parent: { name: 'Our Services', path: '/' },
    service: { name: 'Generative AI Development', type: 'Generative AI development and consulting' },
  },
  '/softwareengineering': {
    title: 'Software Engineering for AI Products | Zemantics',
    description:
      'Cloud-native, API-first software engineering for SaaS and AI products: CI/CD, automated QA, accessible UI/UX and agile delivery, 175+ products built.',
    name: 'Software Engineering',
    parent: { name: 'Our Services', path: '/' },
    service: { name: 'Software Engineering for AI Products', type: 'Custom software development' },
  },
  '/edtech': {
    title: 'EdTech & Higher-Ed AI Solutions | Zemantics',
    description:
      'EdTech software and AI for educational institutions: learning platforms, analytics, LMS integration and decision support for program review and workforce alignment.',
    name: 'EdTech',
    parent: { name: 'Industry', path: '/' },
  },
  '/finance-accounting': {
    title: 'AI for Finance & Accounting | Zemantics',
    description:
      'AI for finance and accounting teams: payment fraud protection, agentic AI for financial operations, intelligent CRM and robotic process automation.',
    name: 'Finance & Accounting',
    parent: { name: 'Industry', path: '/' },
  },
  '/healthcare': {
    title: 'AI for Healthcare Providers | Zemantics',
    description:
      'Custom AI agents and software for healthcare: scheduling, documentation assistance, personalized care plans, remote monitoring and operational analytics.',
    name: 'Healthcare',
    parent: { name: 'Industry', path: '/' },
  },
  '/pharmaceutical': {
    title: 'AI for Pharma & Life Sciences | Zemantics',
    description:
      'AI for pharmaceutical teams: data analysis, NLP, computer vision, drug discovery support, clinical trials, manufacturing, pharmacovigilance and research decisions.',
    name: 'Pharmaceutical',
    parent: { name: 'Industry', path: '/' },
  },
  '/privacy-policy': {
    title: 'Privacy Policy | Zemantics',
    description:
      'How Zemantics LLC collects, uses and protects personal information submitted through zemanticsllc.com, and the choices available to you.',
    name: 'Privacy Policy',
  },
};

// Service pages: FAQ structured data mirrors the visible FAQ sections.
(['aiml', 'dataengineering', 'generativeai', 'softwareengineering'] as const).forEach((key) => {
  SEO[`/${key}`].faq = SERVICES[key].faq;
});

export const NOT_FOUND_SEO: PageSeo = {
  title: 'Page not found | Zemantics',
  description: 'The page you are looking for does not exist or has moved.',
  name: 'Not found',
  noindex: true,
};

export const normalizePath = (pathname: string): string => {
  const p = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return p || '/';
};

export const absoluteUrl = (path: string): string => `${SITE_URL}${path === '/' ? '/' : `${path}/`}`;

const organization = {
  '@type': 'Organization',
  '@id': `${SITE_URL}/#organization`,
  name: 'Zemantics LLC',
  alternateName: 'Zemantics',
  url: `${SITE_URL}/`,
  logo: `${SITE_URL}/Assets/Zemantics-Logo.svg`,
  email: SUITE.email,
  description:
    'Zemantics is an AI and software engineering company. We build AI agents, agentic orchestrators, data decision pipelines, generative AI and custom software for enterprises across industries, and offer the Zemantics Catalyst Suite.',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '125 Village Blvd',
    addressLocality: 'Princeton',
    addressRegion: 'NJ',
    postalCode: '08540',
    addressCountry: 'US',
  },
  contactPoint: [{ '@type': 'ContactPoint', contactType: 'sales', email: SUITE.email, areaServed: 'US', availableLanguage: 'English' }],
  knowsAbout: [
    'Artificial intelligence',
    'Agentic AI',
    'AI agents and agent orchestration',
    'Data decision pipelines',
    'Machine learning',
    'Generative AI',
    'Data engineering',
    'AI software engineering',
    'Research portfolio decision support',
    'Academic program review',
    'Workforce and labor-market analysis',
  ],
};

const website = {
  '@type': 'WebSite',
  '@id': `${SITE_URL}/#website`,
  url: `${SITE_URL}/`,
  name: SITE_NAME,
  publisher: { '@id': `${SITE_URL}/#organization` },
};

export const buildJsonLd = (path: string, seo: PageSeo): object => {
  const url = absoluteUrl(path);
  const graph: any[] = [organization, website];

  graph.push({
    '@type': seo.products ? 'CollectionPage' : seo.pageType || 'WebPage',
    '@id': `${url}#webpage`,
    url,
    name: seo.title,
    description: seo.description,
    isPartOf: { '@id': `${SITE_URL}/#website` },
    about: { '@id': `${SITE_URL}/#organization` },
    inLanguage: 'en-US',
  });

  if (path !== '/') {
    const crumbs = [{ name: 'Home', url: `${SITE_URL}/` }];
    if (seo.parent && seo.parent.path !== '/') crumbs.push({ name: seo.parent.name, url: absoluteUrl(seo.parent.path) });
    crumbs.push({ name: seo.name, url });
    graph.push({
      '@type': 'BreadcrumbList',
      itemListElement: crumbs.map((c, i) => ({ '@type': 'ListItem', position: i + 1, name: c.name, item: c.url })),
    });
  }

  if (seo.service) {
    graph.push({
      '@type': 'Service',
      name: seo.service.name,
      serviceType: seo.service.type,
      description: seo.description,
      provider: { '@id': `${SITE_URL}/#organization` },
      areaServed: 'US',
      url,
    });
  }

  if (seo.products) {
    PRODUCTS.forEach((p) => {
      graph.push({
        '@type': 'SoftwareApplication',
        '@id': `${url}#${p.id}`,
        name: `Zemantics ${p.name}`,
        applicationCategory: 'BusinessApplication',
        description: p.summary,
        url: `${url}#${p.id}`,
        image: `${SITE_URL}${p.logo}`,
        publisher: { '@id': `${SITE_URL}/#organization` },
      });
    });
  }

  if (seo.faq && seo.faq.length) {
    graph.push({
      '@type': 'FAQPage',
      mainEntity: seo.faq.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
  }

  return { '@context': 'https://schema.org', '@graph': graph };
};
